<template>
  <div class="main-collection critical-component-clear">
    <collection-sub-navigation
      :navigation="navigation"
    />

    <h1
      :id="`section-title-${liquid.sectionId}`"
      class="
        main-collection__title
        text-heading-3xl
        text-heading-4xl-desktop
      "
      v-text="liquid.collectionTitle"
    />

    <p
      v-if="liquid.collectionDesc"
      class="
        main-collection__desc
        text-body-l-desktop
      "
      v-html="liquid.collectionDesc"
    />

    <responsive-image
      v-if="liquid.collectionImg?.mobile"
      class="main-collection__img main-collection__img--mobile"
      :image="{src: liquid.collectionImg.mobile}"
      :ratio="0.8"
      sizes="100vw"
    />

    <responsive-image
      v-if="liquid.collectionImg?.desktop"
      class="main-collection__img main-collection__img--desktop"
      :image="{src: liquid.collectionImg.desktop}"
      :ratio="3.71"
      sizes="100vw"
    />

    <div
      v-if="noProducts"
      class="boost-filter__error-message"
      v-text="$string('collection.no_products')"
    />

    <template v-if="boostReady">
      <div class="main-collection__grid grid">
        <template
          v-for="(product, index) of products"
          :key="`product-card-${liquid.sectionId}-${product.id}-${index}`"
        >
          <product-card
            v-if="product.id"
            class="main-collection__product-card"
            :index="index"
            :product="product"
            :collection-url="liquid.collectionUrl"
            :sizes="sizes"
            :swatch-behavior="liquid.swatchBehavior"
            :container-id="liquid.sectionId"
          />
        </template>
      </div>

      <boost-infinite-scroll
        v-if="products.length < totalProduct"
        :choices="choices"
        :collection-id="liquid.collectionId"
        :limit="liquid.productsPerPage"
        :delay="liquid.infiniteScrollDelay"
        @infinite-updated="infiniteUpdated"
      />
    </template>

    <div
      v-else
      class="main-collection__grid grid"
    >
      <template
        v-for="(index) of liquid.collection.productsCount"
        :key="`product-card-${liquid.sectionId}-${index}`"
      >
        <product-card
          class="main-collection__product-card product card"
          :index="index"
          :product="false"
          :sizes="sizes"
          :container-id="liquid.sectionId"
        />
      </template>
    </div>

    <boost-api-call
      build-filter-tree
      :choices="choices"
      :collection-id="liquid.collectionId"
      endpoint="filter"
      :limit="liquid.productsPerPage"
      :shop="$variable('store.localisedPermanentDomain')"
      @boost-updated="boostUpdated"
    />
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main collection component.
 *
 * @param {Number} liquid.collectionId - Collection ID.
 * @param {String} liquid.collectionTitle - Collection title.
 * @param {Number} liquid.productsPerPage - Products per page.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Object} liquid.sortByOptions - Object of sort by options.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import { values as breakpoints } from '~/config/breakpoints'

import CollectionSubNavigation from '~async/collection-sub-navigation/collection-sub-navigation'
import ResponsiveImage from '~global/images/responsive-image'
import ProductCard from '~async/product-card/product-card'
import BoostInfiniteScroll from '~async/main-collection/boost-infinite-scroll'
import BoostApiCall from '~global/boost-api-call/boost-api-call'

import { formatBoostProduct } from '~/helpers/format'

export default {
  name: 'MainCollection',

  components: {
    CollectionSubNavigation,
    ResponsiveImage,
    ProductCard,
    BoostInfiniteScroll,
    BoostApiCall,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        collection: {},
        collectionId: '',
        collectionTitle: '',
        collectionDesc: '',
        collectionImg: {},
        productsPerPage: '',
        sectionId: '',
        sortByOptions: {},
        infiniteScrollDelay: 0,
        swatchBehavior: false,
      }),
    },
    navigation: {
      type: Object,
      default: () => ({
        showCounters: true,
        list: [],
      }),
    },
  },

  data() {
    return {
      boostReady: false,
      choices: null,
      filter: [],
      noProducts: false,
      page: 1,
      products: [],
      totalProduct: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isTiny: (state) => state.index.screen.tiny,
    }),

    /**
     * Compute drawer choices.
     * @returns {String}
     */
    drawerChoices() {
      return this.getChoices()
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `
        (min-width: ${breakpoints.m}) 33vw,
        (min-width: ${breakpoints.s}) 50vw, 100vw
      `
    },
  },

  watch: {

    /**
     * Watch drawer choices to update URL.
     */
    drawerChoices(choices) {
      if (choices === this.choices) {
        return
      }

      this.choices = choices
      this.boostReady = false
      this.updateWindowLocationSearch(this.choices)
    },
  },

  mounted() {
    this.getSearchParams()
    this.setEventListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      updateChoices: 'main-collection/updateChoices',
      updateFilters: 'main-collection/updateFilters',
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getChoices: 'main-collection/getChoices',
      getFilters: 'main-collection/getFilters',
    }),

    /**
     * Check window.location.search on load to see if filter choices exist
     * before making API call.
     */
    getSearchParams() {
      if (!window.location.search.includes('?')) {
        this.choices = ''
        this.boostReady = false
        this.updateChoices(this.choices)

        return
      }

      const urlString = window.location.search.split('?')[1]

      const choicesArray = urlString.split('&').filter((choice) => {
        const filterOptionId = choice.split('=')[0]

        if (
          filterOptionId.includes('pf_') ||
          filterOptionId.includes('page') ||
          (filterOptionId.includes('sort') && choice !== '')
        ) {
          return choice
        }

        return false
      })

      const formatted = choicesArray.map((choice) => `&${choice}`)
      this.choices = formatted.join('')
      this.boostReady = false
      this.updateChoices(this.choices)
    },

    /**
     * Update window location search and browser history after choices updated.
     * @param {String} choices - Updated user choices.
     */
    updateWindowLocationSearch(choices) {
      let url = window.location.href.replace(window.location.search, `?${choices}`)

      if (window.location.search === '') {
        url = window.location.href.replace(window.location.href, `${window.location.href}?${choices}`)
      }

      if (!url.includes('&')) {
        url = url.replace('?', '')
      }

      window.history.pushState({}, '', url)
    },

    /**
     * Update pagination.
     * @param {String} pageNumber - Page number from choices.
     */
    updatePagination(pageNumber) {
      if (pageNumber) {
        if (this.totalProduct > this.products.length) {
          this.page = 1
        }

        return
      }

      this.page = parseInt(pageNumber)
    },

    /**
     * Callback after a successfull API call.
     * @param {Object} data - Response object from the API call.
     */
    boostUpdated(data) {
      this.products = data.products.map((product) => {
        return formatBoostProduct(product)
      })

      this.filter = data.filter.options
      this.totalProduct = data.total_product
      this.noProducts = !this.products.length
      this.setFilterSwatches()
      this.updateFilters(this.filter)
      this.boostReady = true
    },

    infiniteUpdated(data) {
      const products = data.products.map((product) => {
        return formatBoostProduct(product)
      })

      this.products = [...this.products, ...products]
    },

    /**
     * Event handler to close any open filters on body click.
     * @param {Event} event - Click event.
     */
    handleBodyClick(event) {
      const facets = document.querySelectorAll('[js-boost-facet="facet"]')
      const sortBy = document.querySelector('[js-boost-filter="sortBy"]')
      let bodyClick = true

      facets.forEach((facet) => {
        if (!facet.contains(event.target)) {
          return
        }

        bodyClick = false
      })

      if (sortBy !== null && sortBy.contains(event.target)) {
        bodyClick = false
      }

      if (!bodyClick) {
        return
      }

      facets.forEach((facet) => {
        facet.classList.remove(this.$class('active'))
      })

      if (sortBy === null) {
        return
      }

      sortBy.classList.remove(this.$class('active'))
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      window.addEventListener('click', this.handleBodyClick)
      window.onpopstate = () => this.getSearchParams()
    },

    /**
     * Set swatches for use in Boost Filter.
     */
    setFilterSwatches() {
      const data = this.filter.find((option) => {
        return option.filterType === 'opt_color'
      })

      if (!data) {
        return
      }

      const swatches = data.values.map((swatch) => {
        const key = swatch.key.toLowerCase()

        const formatted = {
          key,
          swatch: `color-${key}_12x12.png`,
        }

        return formatted
      })

      window.cnvs = window.cnvs || {}
      window.cnvs.filterSwatches = swatches
    },
  },
}
</script>

<style lang="scss">
@import './main-collection';
</style>
